import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {
  DangerInformation,
  DeclarationStatus,
  GoodsClassification,
  HarbourDuesContainerClass,
  HarbourDuesEquipmentLength,
  HarbourDuesEquipmentType,
  HarbourDuesStevedore,
  LinerService,
  Port
} from "@portbase/bezoekschip-service-typescriptmodels";

export type HarbourDuesCargoHandlingCountForm = {
  count: FormControl<number | null>;
  empty: FormControl<boolean | null>;
  equipmentLength: FormControl<HarbourDuesEquipmentLength | null>;
  equipmentType: FormControl<HarbourDuesEquipmentType | null>;
}

export type HarbourDuesGoodsItemForm = {
  cargoType: FormControl<string | null>;
  dangerInformation: FormControl<DangerInformation | null>;
  goodsClassification: FormControl<GoodsClassification | null>;
  goodsCodeType: FormControl<string | null>;
  portOfHandling: FormControl<Port | null>;
  shipToShip: FormControl<boolean | null>;
  weight: FormControl<number | null>;
}

export type HarbourDuesContainerHandlingCountForm = {
  containerType: FormControl<HarbourDuesContainerClass | null>;
  full: FormControl<number | null>;
  empty: FormControl<number | null>;
}

export type HarbourDuesBerthVisitForm = {
  berthVisitId: FormControl<string | null>;
  dischargedCargoCounts: FormArray<FormGroup<HarbourDuesCargoHandlingCountForm>>;
  loadedCargoCounts: FormArray<FormGroup<HarbourDuesCargoHandlingCountForm>>;
  loadedGoods: FormArray<FormGroup<HarbourDuesGoodsItemForm>>;
  dischargedGoods: FormArray<FormGroup<HarbourDuesGoodsItemForm>>;
  stevedore: FormControl<HarbourDuesStevedore | null>;
  loadedContainerHandling?: FormArray<FormGroup<HarbourDuesContainerHandlingCountForm>>;
  dischargedContainerHandling?: FormArray<FormGroup<HarbourDuesContainerHandlingCountForm>>;
}

export interface HarbourDuesIncidentalForm {
  incidentalType: FormControl<string | null>;
  incidentalAmount: FormControl<number | null>;
  incidentalReason: FormControl<string | null>;
}

export type HarbourDuesDiscountForm = {
  discountType: FormControl<string | null>;
  greenAward: FormControl<boolean | null>;
  linerService: FormControl<LinerService | null>;
  shortSea: FormControl<boolean | null>;
}

export type HarbourDuesSpecialTariffForm = {
  specialTariffType: FormControl<string | null>;
  specialTariffReason: FormControl<string | null>;
}

export enum DiscountSelection {
  NO_DISCOUNT,
  DISCOUNT,
  SPECIAL_FEE,
  EXEMPTION
}

export interface HarbourDuesDeclarationTab {
  crn: string;
  statementNumber: number;
  invoiceStart: string;
  invoiceEnd: string;
}

export interface HarbourDuesDeclarationStatus {
  crn: string;
  statementNumber: number;
  status: DeclarationStatus;
  rejectReason?: string;
}

export interface CopyDataConfirmedEvent {
  index: number;
  terminalCode: string;
  type: string;
}

export enum UnitOfMass {
  KILOGRAM, METRIC_TON
}

export const HarbourDuesCargoType = {
  DRY_BULK: "DRY_BULK",
  LIQUID_BULK: "LIQUID_BULK",
  CONTAINERS_ROLL_ON_ROLL_OFF: "CONTAINERS_ROLL_ON_ROLL_OFF",
  CONTAINERS_LIFT_ON_LIFT_OFF: "CONTAINERS_LIFT_ON_LIFT_OFF",
  FLATS: "FLATS",
  FLATS_ROLL_ON_ROLL_OFF: "FLATS_ROLL_ON_ROLL_OFF",
  FLATS_LIFT_ON_LIFT_OFF: "FLATS_LIFT_ON_LIFT_OFF",
  LASH_EQUIPMENT: "LASH_EQUIPMENT",
  ROLL_ON_ROLL_OFF: "ROLL_ON_ROLL_OFF",
  BREAKBULK: "BREAKBULK",
  GENERAL_CARGO: "GENERAL_CARGO",
} as const;

export const HarbourDuesContainerCargoType = {
  CONTAINERS_ROLL_ON_ROLL_OFF: "CONTAINERS_ROLL_ON_ROLL_OFF",
  CONTAINERS_LIFT_ON_LIFT_OFF: "CONTAINERS_LIFT_ON_LIFT_OFF",
  FLATS: "FLATS",
  FLATS_ROLL_ON_ROLL_OFF: "FLATS_ROLL_ON_ROLL_OFF",
  FLATS_LIFT_ON_LIFT_OFF: "FLATS_LIFT_ON_LIFT_OFF",
} as const;

export const HarbourDuesBulkCargoType = {
  DRY_BULK: "DRY_BULK",
  LIQUID_BULK: "LIQUID_BULK",
  LASH_EQUIPMENT: "LASH_EQUIPMENT",
  ROLL_ON_ROLL_OFF: "ROLL_ON_ROLL_OFF",
  BREAKBULK: "BREAKBULK",
  GENERAL_CARGO: "GENERAL_CARGO",
} as const;

export const HarbourDuesExemption = {
  TRAINING: "TRAINING",
  WAR: "WAR",
  TUG: "TUG",
  CRUISE: "CRUISE",
  REPAIR: "REPAIR",
  CREW: "CREW",
  SCRAP: "SCRAP",
  CONTRACTOR: "CONTRACTOR"
} as const;
